import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { getCroppedImageBlob } from "../lib/cropImage";
import { ZoomIn } from "lucide-react";
import { toast } from "sonner";

/**
 * Square-crop-by-default dialog shown between "pick a file" and "upload".
 * Renders the same on desktop web and inside the Capacitor mobile shell
 * (react-easy-crop handles both mouse drag and touch/pinch natively), so
 * the exported crop looks identical everywhere the image is later
 * displayed with `object-cover` in a fixed-aspect box.
 */
export default function ImageCropDialog({
  open,
  imageSrc,
  aspect = 1,
  outputSize = 1200,
  title = "Crop image",
  onCancel,
  onCropped,          // (blob: Blob) => void
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels, { outputSize });
      onCropped(blob);
    } catch {
      toast.error("Couldn't crop that image — try a different file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="image-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
        </DialogHeader>
        <div className="relative w-full aspect-square bg-neutral-900 rounded-2xl overflow-hidden" data-testid="image-crop-stage">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape="rect"
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className="flex items-center gap-3 px-1">
          <ZoomIn className="w-4 h-4 text-neutral-500 shrink-0" />
          <input
            type="range" min={1} max={3} step={0.01} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-brand-sky"
            data-testid="image-crop-zoom"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" className="flex-1 h-11 rounded-2xl font-bold" data-testid="image-crop-cancel">
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={busy || !croppedAreaPixels}
                  className="flex-1 h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold" data-testid="image-crop-confirm">
            {busy ? "Cropping…" : "Crop & Upload"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
